/**
 * fileList.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import * as React from "react";
import { useParams } from "react-router-dom";
import { DateTime } from "luxon";

import MenuIcon from "@mui/icons-material/Menu";
import { CircularProgress, IconButton, TextField } from "@mui/material";

import { AuthContext } from "../auth/auth.context";
import { GroupNavContext } from "../chat/groupNav.context";

//import { ErrorSnackBar } from "../components/errorSnackBar.component";

import { StoredFile, classForType, sizeDisplay } from "../models/file";
import { GroupMembership } from "../models/group";
import { groupServiceFactory } from "../services/group.service";
import { FileDownloader } from "../chat/fileDownloader.component";

interface GroupFileListComponentParams {
  groupID: string,
  [key: string]: string | undefined
}
export function GroupFileListComponent(): React.ReactElement<any, any> {
  const groupNavContext = React.useContext<GroupNavContext>(GroupNavContext);
  const authContext = React.useContext<AuthContext>(AuthContext);
  const { groupID } = useParams<GroupFileListComponentParams>() as { groupID: string };
  const [files, setFiles] = React.useState<Array<StoredFile>>([]);
  const [fileData, setFileData] = React.useState<{ fileID: string, keyID: string }>({ fileID: "", keyID: "" });
  const [filterText, setFilterText] = React.useState<string>("");
  const [timer, setTimer] = React.useState<NodeJS.Timeout | null>(null);
  const [, setLoading] = React.useState(false);

  // in case we land on this page
  React.useEffect(function () {
    if (!groupNavContext.groupMemberships && !groupNavContext.directMessages) { return; }
    if (groupNavContext.groupMemberships.length <= 0 && groupNavContext.directMessages.length <= 0) { return; }
    const membership: GroupMembership | undefined = groupNavContext.groupMemberships?.find(m =>
      m.groupID === groupID) ||
      groupNavContext.directMessages?.find(m => m.groupID === groupID);
    if (!membership) {
      console.error("No membership with groupID:", groupID);
      return;
    }
    if (membership.groupID !== groupNavContext.currentGroup?.groupID) {
      groupNavContext.setCurrentGroup(membership);
    }
  }, [groupID, groupNavContext.groupMemberships.length, groupNavContext.directMessages.length]);

  React.useEffect(function () {
    console.log("groupID:", groupID);
    const accountID = authContext.membership?.accountID;
    if (!accountID) { return; }
    const groupService = groupServiceFactory();

    groupService.getGroupFiles(accountID, groupID, "", 100).then(files => {
      setFiles(files);
    }, err => {
      console.error("Error fetching files:", err);
    });

  }, [groupID, authContext.membership]);

  function searchValueChange(event: React.SyntheticEvent) {
    const value = (event.target as HTMLInputElement).value;
    setFilterText(value);
    if (timer) {
      clearTimeout(timer);
    }
    setTimer(setTimeout(async () => {
      try {
        setLoading(true);
        console.log("fetching files matching:", value);
        const accountID = authContext.membership?.accountID;
        if (!accountID) { return; }
        const groupService = groupServiceFactory();

        const files = await groupService.getGroupFiles(accountID, groupID, value, 100);
        setFiles(files);
      } catch (err) {
        console.error("Error fetching members:", err);
        //setErrMsg((err as Error).message);
      } finally {
        setLoading(false);
        setTimer(null);
      }
    }, 1000));
  }

  function downloadFile(index: number) {
    const file = files[index];
    setFileData({ fileID: file._id, keyID: file.keyID });
  }

  async function deleteFile(index: number) {
    const groupMembership = groupNavContext.currentGroup;
    if (!groupMembership) { return; }
    const file = files[index];
    try {
      await groupServiceFactory().deleteFile(
        groupMembership.accountID, groupMembership.groupID, file._id);

      const newFiles = Array.from(files);
      newFiles.splice(index, 1);
      setFiles(newFiles);
    } catch (err) {
      console.error("Error deleting file:", err);
      throw err;
    }
  }

  return <div className="group-file-list-container">
    <header className="group-header-nav attached-left-nav">
      <IconButton className="left-nav-outer-toggle" size="small"
        disableRipple disableTouchRipple
        onClick={groupNavContext.toggleGroupList}>
        <MenuIcon />
      </IconButton>
      <h3>Group Files: {groupNavContext.currentGroup?.groupname || ""}</h3>
    </header>
    <div className="search-container">
      <TextField className="file-search" label="Filter" variant="outlined" fullWidth
        size="small" value={filterText} onChange={searchValueChange} />
    </div>
    <div>
      {files.map((file, index) =>
        <GroupFileComponent key={file._id} index={index} file={file}
          onDelete={deleteFile}
          onDownload={downloadFile} />
      )}
    </div>
    <FileDownloader groupID={groupID} {...fileData}
      didTrigger={() => setFileData({ fileID: "", keyID: "" })}
      onError={() => setFileData({ fileID: "", keyID: "" })} />
  </div>;
  /*<IconButton size="small" disableRipple disableTouchRipple>
              <i className="fa-solid fa-download"></i>
            </IconButton>
            */
}

interface FileProps {
  index: number,
  file: StoredFile,
  onDownload(index: number): void,
  onDelete(index: number): Promise<void>
}

function GroupFileComponent(props: FileProps): React.ReactElement<any, any> {
  const [deleting, setDeleting] = React.useState<boolean>(false);
  const typeClasses = classForType(props.file.contentType);

  async function doDelete(event: React.SyntheticEvent) {
    event.stopPropagation();
    event.preventDefault();
    if (deleting) { return; }
    try {
      setDeleting(true);
      await props.onDelete(props.index);
    } catch (err) {
      void err;
      setDeleting(false);
    } // if success, don't call setDeleting()
  }

  return <article className="group-file-container"
      data-file-id={props.file._id} data-file-path={props.file.path} 
      aria-label="Download File" onClick={() => props.onDownload(props.index)}>
    <i className={`file-icon fa-solid ${typeClasses}`}></i>
    <div className="file-info">
      <div className="file-name">{props.file.filename}</div>
      <div className="file-size">{sizeDisplay(props.file.filesize, 2)}</div>
      {props.file.created ?
        <div className="file-timestamp">{props.file.created.toLocaleString(DateTime.DATETIME_MED)}</div> :
        <div className="file-timestamp">&nbsp;</div>}
    </div>
    <div className="right-controls">
      {deleting ?
        <CircularProgress size={20} /> :
        <IconButton aria-label="Delete File" size="small" disableRipple disableTouchRipple onClick={doDelete}>
          <i className="fa-solid fa-trash-can"></i>
        </IconButton>}
    </div>
  </article>;
}
